import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { $canEditComplex, $isSettlementTenant, $user } from '../store/auth';
import HomeGreeting from '../components/HomeGreeting';
import { $activeTenant, $activeTenantAccessLevel } from '../store/tenantContext';
import { getObjectKindLabels } from '../utils/complexKind';
import { $activeComplexId, $complexes, fetchResidentialComplexesFx, complexSelected } from '../store/complexes';
import ComplexesToolbar from '../components/Complexes/ComplexesToolbar';
import ResidentialComplexList from '../components/Complexes/ResidentialComplexList';
import ObjectsMapPanel from '../components/Complexes/ObjectsMapPanel';
import { useNavigate, useLocation } from 'react-router-dom';
import { reportsApi } from '../api/reports';
import { localMonthStartDate } from '../utils/dates';
import { buildFileName, downloadBlobAsFile } from '../utils/download';
import { showSoftSuccess } from '../utils/softSuccess';
import { devicesApi } from '../api/devices';
import { gatewaysApi } from '../api/gateways';
import { userHasRole } from '../config/roles';
import OnboardingPage from './OnboardingPage';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Button,
  TextField,
  Menu,
  MenuItem,
  Pagination,
  Divider,
  Fade,
  Grid,
  Skeleton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  FormGroup,
  IconButton,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import OpacityIcon from '@mui/icons-material/Opacity';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import DeviceThermostatRoundedIcon from '@mui/icons-material/DeviceThermostatRounded';
import BoltIcon from '@mui/icons-material/Bolt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
};
const writeCookie = (name, value, days = 365) => {
  if (typeof document === 'undefined') return;
  const maxAge = Math.max(1, days) * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}`;
};

const AnimatedNumber = ({ value, duration = 700 }) => {
  const [display, setDisplay] = useState(Number(value || 0));
  const prevRef = React.useRef(Number(value || 0));

  useEffect(() => {
    const start = Number(prevRef.current || 0);
    const end = Number(value || 0);
    if (start === end) {
      setDisplay(end);
      return;
    }
    let rafId;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(start + (end - start) * eased);
      setDisplay(nextValue);
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        prevRef.current = end;
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [value, duration]);

  return <>{display}</>;
};

const AlertSettingsDialog = React.memo(
  ({
    open,
    loading,
    error,
    initialSettings,
    onClose,
    onSave,
  }) => {
    const [form, setForm] = useState(initialSettings);
    const [emailValues, setEmailValues] = useState([]);
    const [telegramValues, setTelegramValues] = useState([]);
    const [emailInputValue, setEmailInputValue] = useState('');
    const [telegramInputValue, setTelegramInputValue] = useState('');
    const [emailError, setEmailError] = useState('');
    const [quietStart, setQuietStart] = useState('');
    const [quietEnd, setQuietEnd] = useState('');
    const localTimeZone = useMemo(() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
      } catch {
        return 'local';
      }
    }, []);

    useEffect(() => {
      if (open) {
        setForm(initialSettings);
        setEmailValues(initialSettings?.email_recipients || []);
        setTelegramValues(initialSettings?.telegram_chat_ids || []);
        setEmailInputValue('');
        setTelegramInputValue('');
        setEmailError('');
        const normalizeTime = (value) => {
          if (!value) return '';
          const trimmed = String(value);
          return trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed;
        };
        setQuietStart(normalizeTime(initialSettings?.quiet_hours_start));
        setQuietEnd(normalizeTime(initialSettings?.quiet_hours_end));
        if (!initialSettings?.quiet_timezone) {
          setForm((prev) => ({ ...prev, quiet_timezone: localTimeZone }));
        }
      }
    }, [open, initialSettings, localTimeZone]);

    const splitRecipients = useCallback((value) => {
      if (!value) return [];
      return value
        .split(/[,\s]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    }, []);

    const isValidEmail = useCallback((value) => {
      if (!value) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }, []);

    const isValidTelegramChatId = useCallback((value) => {
      if (!value) return false;
      return /^-?\d{5,}$/.test(String(value).trim());
    }, []);

    const mergeTokens = useCallback((current, tokens, validateFn) => {
      const next = [...(current || [])];
      const invalid = [];
      (tokens || []).forEach((token) => {
        const cleaned = String(token || '').trim();
        if (!cleaned) return;
        if (validateFn && !validateFn(cleaned)) {
          invalid.push(cleaned);
          return;
        }
        if (!next.includes(cleaned)) {
          next.push(cleaned);
        }
      });
      return { next, invalid };
    }, []);

    const applyTokenInput = useCallback((rawValue, setValues, setInputValue, validateFn, setError) => {
      if (!/[\s,]/.test(rawValue || '')) {
        return false;
      }
      const tokens = splitRecipients(rawValue);
      if (!tokens.length) return false;
      setValues((prev) => mergeTokens(prev, tokens, validateFn).next);
      const { invalid } = mergeTokens([], tokens, validateFn);
      if (setError) {
        if (invalid.length) {
          setError(
            validateFn === isValidEmail
              ? `Некорректный email: ${invalid.join(', ')}`
              : `Некорректный chat_id: ${invalid.join(', ')}`
          );
        } else {
          setError('');
        }
      }
      setInputValue('');
      return true;
    }, [splitRecipients, mergeTokens, isValidEmail]);

    const handleSave = useCallback(() => {
      const pendingEmails = splitRecipients(emailInputValue);
      const pendingTelegram = splitRecipients(telegramInputValue);
      const emailMerged = mergeTokens(emailValues, pendingEmails, isValidEmail);
      const telegramMerged = mergeTokens(telegramValues, pendingTelegram, isValidTelegramChatId);

      if (emailMerged.invalid.length) {
        setEmailError(`Некорректный email: ${emailMerged.invalid.join(', ')}`);
        return;
      }
      if (telegramMerged.invalid.length) {
        setEmailError(`Некорректный chat_id: ${telegramMerged.invalid.join(', ')}`);
        return;
      }

      setEmailValues(emailMerged.next);
      setTelegramValues(telegramMerged.next);
      setEmailInputValue('');
      setTelegramInputValue('');
      setEmailError('');

      const normalizeTime = (value) => {
        if (!value) return null;
        const trimmed = String(value).trim();
        if (!trimmed) return null;
        return trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed;
      };

      onSave({
        ...form,
        email_recipients: emailMerged.next,
        telegram_chat_ids: telegramMerged.next,
        quiet_hours_start: normalizeTime(quietStart),
        quiet_hours_end: normalizeTime(quietEnd),
        quiet_timezone: form.quiet_timezone || localTimeZone,
        daily_summary_enabled: Boolean(form.daily_summary_enabled),
        daily_summary_time: form.daily_summary_enabled
          ? normalizeTime(form.daily_summary_time)
          : null,
      });
    }, [
      onSave,
      form,
      emailValues,
      telegramValues,
      emailInputValue,
      telegramInputValue,
      quietStart,
      quietEnd,
      localTimeZone,
      splitRecipients,
      mergeTokens,
      isValidEmail,
      isValidTelegramChatId,
    ]);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid var(--border-color)',
            background:
              'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(241,245,249,0.95))',
            boxShadow: 'none',
          },
        }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                background: 'rgba(59, 130, 246, 0.12)',
                color: '#1D4ED8',
              }}
            >
              <AutoGraphRoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Умные уведомления
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Быстрые оповещения при сбоях в сети УСПД
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: 'var(--border-color)',
                background: 'rgba(255,255,255,0.9)',
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Триггеры
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.enabled)}
                        onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                      />
                    }
                    label="Включить уведомления о состоянии шлюзов"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.notify_down)}
                        onChange={(e) => setForm((prev) => ({ ...prev, notify_down: e.target.checked }))}
                      />
                    }
                    label="Сообщать, когда шлюз DOWN"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.notify_degraded)}
                        onChange={(e) => setForm((prev) => ({ ...prev, notify_degraded: e.target.checked }))}
                      />
                    }
                    label="Сообщать, когда шлюз DEGRADED"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(form.notify_recovered)}
                        onChange={(e) => setForm((prev) => ({ ...prev, notify_recovered: e.target.checked }))}
                      />
                    }
                    label="Сообщать, когда шлюз восстановился (UP)"
                  />
                </FormGroup>
              </Stack>
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: 'var(--border-color)',
                background: 'rgba(255,255,255,0.9)',
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Каналы
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={emailValues}
                  inputValue={emailInputValue}
                  onChange={(_, newValue) => setEmailValues(newValue)}
                  onInputChange={(_, newInputValue) => {
                    if (!applyTokenInput(newInputValue, setEmailValues, setEmailInputValue, isValidEmail, setEmailError)) {
                      setEmailInputValue(newInputValue);
                    }
                  }}
                  filterOptions={(options) => options}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Email получатели"
                      placeholder="email1@company.ru, email2@company.ru"
                      helperText={emailError || 'Enter, пробел или запятая добавляют адрес'}
                      error={Boolean(emailError)}
                      FormHelperTextProps={{ sx: emailError ? { color: '#B91C1C' } : undefined }}
                      fullWidth
                      size="small"
                      onBlur={() => {
                        if (!emailInputValue.trim()) return;
                        applyTokenInput(
                          `${emailInputValue},`,
                          setEmailValues,
                          setEmailInputValue,
                          isValidEmail,
                          setEmailError
                        );
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ' || event.key === ',') {
                          event.preventDefault();
                          applyTokenInput(
                            `${emailInputValue},`,
                            setEmailValues,
                            setEmailInputValue,
                            isValidEmail,
                            setEmailError
                          );
                        }
                      }}
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={telegramValues}
                  inputValue={telegramInputValue}
                  onChange={(_, newValue) => {
                    const cleaned = (newValue || [])
                      .map((item) => String(item || '').trim())
                      .filter(Boolean);
                    const { next, invalid } = mergeTokens([], cleaned, isValidTelegramChatId);
                    setTelegramValues(next);
                    if (invalid.length) {
                      setEmailError(`Некорректный chat_id: ${invalid.join(', ')}`);
                    } else {
                      setEmailError('');
                    }
                  }}
                  onInputChange={(_, newInputValue) => {
                    if (!applyTokenInput(
                      newInputValue,
                      setTelegramValues,
                      setTelegramInputValue,
                      isValidTelegramChatId,
                      setEmailError
                    )) {
                      setTelegramInputValue(newInputValue);
                    }
                  }}
                  filterOptions={(options) => options}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Telegram chat_id"
                      placeholder="123456789, -1001234567890"
                      helperText="Введите ID и нажмите Enter или Сохранить. В группе: /showchatid"
                      fullWidth
                      size="small"
                      onBlur={() => {
                        if (!telegramInputValue.trim()) return;
                        applyTokenInput(
                          `${telegramInputValue},`,
                          setTelegramValues,
                          setTelegramInputValue,
                          isValidTelegramChatId,
                          setEmailError
                        );
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ' || event.key === ',') {
                          event.preventDefault();
                          applyTokenInput(
                            `${telegramInputValue},`,
                            setTelegramValues,
                            setTelegramInputValue,
                            isValidTelegramChatId,
                            setEmailError
                          );
                        }
                      }}
                    />
                  )}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label="Email" size="small" />
                  <Chip label="Telegram" size="small" />
                  <Chip label="Смена статуса" size="small" />
                </Stack>
              </Stack>
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: 'var(--border-color)',
                background: 'rgba(255,255,255,0.9)',
              }}
            >
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Как получить Telegram chat_id
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  1. Добавьте бота в нужную группу Telegram.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  2. В группе напишите команду <strong>/showchatid</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3. Бот ответит ID — скопируйте его в поле выше.
                </Typography>
              </Stack>
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: 'var(--border-color)',
                background: 'rgba(255,255,255,0.9)',
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Режим тишины
                </Typography>
                <TextField
                  label="Режим"
                  value={form.quiet_mode || 'none'}
                  onChange={(e) => setForm((prev) => ({ ...prev, quiet_mode: e.target.value }))}
                  size="small"
                  select
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="none">Выключен</MenuItem>
                  <MenuItem value="silence">Тишина (не слать)</MenuItem>
                  <MenuItem value="digest">Дайджест (копить)</MenuItem>
                </TextField>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    label="Начало"
                    type="time"
                    value={quietStart}
                    onChange={(e) => setQuietStart(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 140 }}
                  />
                  <TextField
                    label="Конец"
                    type="time"
                    value={quietEnd}
                    onChange={(e) => setQuietEnd(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 140 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Время указывается в вашей временной зоне: {localTimeZone}. Если начало больше конца — значит режим через ночь.
                </Typography>
              </Stack>
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2.5,
                borderColor: 'var(--border-color)',
                background: 'rgba(255,255,255,0.9)',
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Ежедневная статистика
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.daily_summary_enabled)}
                      onChange={(e) => setForm((prev) => ({ ...prev, daily_summary_enabled: e.target.checked }))}
                    />
                  }
                  label="Отправлять ежедневную сводку по приборам"
                />
                <TextField
                  label="Время отправки"
                  type="time"
                  value={form.daily_summary_time || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, daily_summary_time: e.target.value }))}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                  disabled={!form.daily_summary_enabled}
                />
                <Typography variant="caption" color="text.secondary">
                  Сводка приходит по онлайн/оффлайн/без показаний. Время — {localTimeZone}.
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Отмена
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
const Home = () => {
  const user = useUnit($user);
  const canEditComplex = useUnit($canEditComplex);
  const isSettlementTenant = useUnit($isSettlementTenant);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );
  const activeTenant = useUnit($activeTenant);
  const activeTenantAccessLevel = useUnit($activeTenantAccessLevel);
  const navigate = useNavigate();
  const location = useLocation();
  const [complexes, pending, selectComplex] = useUnit([$complexes, fetchResidentialComplexesFx.pending, complexSelected]);
  const activeComplexId = useUnit($activeComplexId);
  // 👉 активная вкладка
  const [activeTab, setActiveTab] = useState('Список ЖК');

  useEffect(() => {
    setActiveTab((prev) => {
      if (prev === 'Список ЖК' || prev === 'Список посёлков') {
        return labels.complexListTab;
      }
      return prev;
    });
  }, [labels.complexListTab]);
  useEffect(() => {
    if (!complexes?.length) fetchResidentialComplexesFx();
  }, []); // eslint-disable-line
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'problems' && activeTab !== 'Проблемы') {
      setActiveTab('Проблемы');
    }
  }, [location.search, activeTab]);
  const items = (complexes || []).map((c) => ({
    id: c.id,
    name: c.name,
    complex_kind: c.complex_kind || 'mdu',
    addresses: (c.buildings || []).map((b) => b.address).filter(Boolean),
    counters: {
      cold: c.device_counts?.hvs ?? 0,
      hot: c.device_counts?.gvs ?? 0,
      heat: c.device_counts?.heating ?? 0,
      heatAllocators: c.device_counts?.heat_allocators ?? 0,
      electricity: c.device_counts?.electricity ?? 0,
      all: c.device_counts?.total ?? 0
    },
    incidents: c.incidents ?? {
      leak: 0, lowBattery: 0, tamper: 0, inspection: 0, break: 0,
      screenBlock: 0, noReadings: 0,
    },
  }));
  const complexNameMap = useMemo(() => {
    const map = {};
    (complexes || []).forEach((c) => {
      map[String(c.id)] = c.name || '—';
    });
    return map;
  }, [complexes]);
  const summary = useMemo(() => {
    const totals = {
      complexes: complexes?.length || 0,
      buildings: 0,
      devices: 0,
      hvs: 0,
      gvs: 0,
      heating: 0,
      heatAllocators: 0,
      electricity: 0,
    };
    (complexes || []).forEach((c) => {
      totals.buildings += (c.buildings || []).length;
      const counts = c.device_counts || {};
      totals.hvs += counts.hvs || 0;
      totals.gvs += counts.gvs || 0;
      totals.heating += counts.heating || 0;
      totals.heatAllocators += counts.heat_allocators || 0;
      totals.electricity += counts.electricity || 0;
      totals.devices += counts.total || 0;
    });
    return totals;
  }, [complexes]);
  const countIncidents = useCallback((incidents = {}) => {
    const keys = ['leak', 'lowBattery', 'tamper', 'inspection', 'break', 'screenBlock', 'noReadings'];
    return keys.reduce((sum, key) => sum + (Number(incidents?.[key]) || 0), 0);
  }, []);
  const problemsItems = useMemo(
    () => items.filter((item) => countIncidents(item.incidents) > 0),
    [items, countIncidents]
  );
  const makeSeries = useCallback((seed) => {
    const base = Math.max(6, seed % 12);
    return Array.from({ length: 7 }).map((_, idx) => {
      const wave = Math.sin((idx + 1) * 0.9) * 3;
      return Math.max(2, base + wave + (seed % 3));
    });
  }, []);
  const indicatorCards = [
    {
      key: 'hvs',
      label: 'ХВС',
      value: summary.hvs,
      color: '#0EA5A4',
      icon: OpacityIcon,
      series: makeSeries(summary.hvs),
    },
    {
      key: 'gvs',
      label: 'ГВС',
      value: summary.gvs,
      color: '#EF4444',
      icon: WaterDropIcon,
      series: makeSeries(summary.gvs + 3),
    },
    {
      key: 'heating',
      label: 'Отопление',
      value: summary.heating,
      color: '#F97316',
      icon: LocalFireDepartmentIcon,
      series: makeSeries(summary.heating + 5),
    },
    {
      key: 'heatAllocators',
      label: 'УРТ',
      value: summary.heatAllocators,
      color: '#6366F1',
      icon: DeviceThermostatRoundedIcon,
      series: makeSeries(summary.heatAllocators + 4),
    },
    {
      key: 'electricity',
      label: 'Эл-во',
      value: summary.electricity,
      color: '#FACC15',
      icon: BoltIcon,
      series: makeSeries(summary.electricity + 7),
    },
  ];
  const renderSparkline = (values, color) => {
    if (!values?.length) return null;
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = Math.max(1, max - min);
    const points = values
      .map((v, i) => {
        const x = (i / (values.length - 1)) * 72;
        const y = 20 - ((v - min) / range) * 16;
        return `${x},${y}`;
      })
      .join(' ');
    return (
      <svg width="80" height="24" viewBox="0 0 72 24">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
    );
  };
  const formatGatewayDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('ru-RU');
    } catch {
      return value;
    }
  };
  const gatewayStatusColor = (status) => {
    if (status === 'UP') return 'success';
    if (status === 'DEGRADED') return 'warning';
    if (status === 'DOWN') return 'error';
    return 'default';
  };
  const isContractor = userHasRole(user, 'contractor');
  const canManage = canEditComplex;
  const canWrite = canManage;
  const onboardingKey = useMemo(() => {
    const tenantKey = activeTenant?.tenant_id || activeTenant?.tenant_schema || 'default';
    return `onboarding_seen_${tenantKey}`;
  }, [activeTenant?.tenant_id, activeTenant?.tenant_schema]);
  const [onboardingSeen, setOnboardingSeen] = useState(() => readCookie(onboardingKey) === '1');
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    setOnboardingSeen(readCookie(onboardingKey) === '1');
  }, [onboardingKey]);
  useEffect(() => {
    if (
      !showOnboarding &&
      canManage &&
      !pending &&
      (complexes?.length ?? 0) === 0 &&
      !onboardingSeen
    ) {
      setShowOnboarding(true);
    }
    if (showOnboarding && (complexes?.length ?? 0) > 0) {
      setShowOnboarding(false);
    }
  }, [showOnboarding, canManage, pending, complexes, onboardingSeen]);
  const markOnboardingSeen = useCallback(() => {
    writeCookie(onboardingKey, '1');
    setOnboardingSeen(true);
  }, [onboardingKey]);
  const handleOnboardingDismiss = useCallback(() => {
    markOnboardingSeen();
    setShowOnboarding(false);
  }, [markOnboardingSeen]);
  const quickActionCount = useMemo(() => {
    let count = 1; // export
    if (canManage) count += 2;
    if (canWrite) count += 1;
    return count;
  }, [canManage, canWrite]);
  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      if (tab === 'Проблемы') {
        navigate('/home?tab=problems', { replace: true });
      } else if (location.search.includes('tab=')) {
        navigate('/home', { replace: true });
      }
    },
    [navigate, location.search]
  );
  const renderTab = () => {
    switch (activeTab) {
      case labels.complexListTab:
      case 'Список ЖК':
      case 'Список посёлков':
        return (
          <ResidentialComplexList
            items={items}
            loading={pending}
            onReload={() => fetchResidentialComplexesFx()}
            emptyActionLabel={canManage ? labels.addComplex : undefined}
            onEmptyAction={
              canManage ? () => navigate('/admin/onboarding') : undefined
            }
            onItemClick={(id) => {
              selectComplex(String(id));
              navigate(`/complex/${id}`);
            }}
          />
        );
      case 'Карта объектов':
        return (
          <ObjectsMapPanel
            complexes={complexes}
            loading={pending}
            settlement={isSettlementTenant}
            onOpenBuilding={(_complexId, buildingId) => {
              navigate(`/building/${buildingId}/corps`);
            }}
            onOpenGateway={() => navigate('/gateways')}
          />
        );
      case 'Проблемы':
        if (pending) {
          return (
            <ResidentialComplexList
              items={[]}
              loading
              onReload={() => fetchResidentialComplexesFx()}
            />
          );
        }
        if (problemsItems.length === 0) {
          return (
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid var(--border-color)',
                background: 'var(--surface-bg)',
              }}
            >
              <Stack spacing={1.5} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Сейчас всё спокойно
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Активных проблем по комплексам не обнаружено.
                  </Typography>
                </Stack>
                <Button variant="outlined" onClick={() => navigate('/gateways')}>
                  Что сделать дальше
                </Button>
              </Stack>
            </Paper>
          );
        }
        return (
          <ResidentialComplexList
            items={problemsItems}
            loading={pending}
            onReload={() => fetchResidentialComplexesFx()}
            onItemClick={(id) => {
              selectComplex(String(id));
              navigate(`/complex/${id}`);
            }}
          />
        );
      case 'Список УСПД':
        return (
          <Paper
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid var(--border-color)',
              background: 'var(--surface-bg)',
            }}
          >
            {uspdError ? (
              <Alert severity="error">{uspdError}</Alert>
            ) : null}
            {uspdLoading ? (
              <Stack spacing={1.5}>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </Stack>
            ) : uspdItems.length === 0 ? (
              <Stack spacing={1}>
                <Typography color="text.secondary">
                  УСПД не найдены. Добавьте шлюзы в разделе «Шлюзы».
                </Typography>
                <Box>
                  <Button variant="outlined" size="small" onClick={() => navigate('/gateways')}>
                    Открыть раздел «Шлюзы»
                  </Button>
                </Box>
              </Stack>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Название</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Последний сигнал</TableCell>
                      <TableCell>Хост</TableCell>
                      <TableCell>{labels.complex}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uspdItems.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.name || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={gatewayStatusColor(row.status)} label={row.status || '—'} />
                        </TableCell>
                        <TableCell>{formatGatewayDate(row.last_seen_at)}</TableCell>
                        <TableCell>{row.host || '—'}</TableCell>
                        <TableCell>{complexNameMap[row.residential_complex_id] || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        );
      case 'Отчёты':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper
                className="glass-sheen"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid var(--border-color)',
                  background: 'var(--surface-bg)',
                  height: '100%',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: 'rgba(37, 99, 235, 0.12)',
                      color: '#1D4ED8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DescriptionRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Отчёт по потреблению
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`Экспорт потребления по ${labels.complex.toLowerCase()} с базовыми настройками периода`}
                    </Typography>
                  </Box>
                </Stack>
                {reportError && <Alert severity="error" sx={{ mb: 1.5 }}>{reportError}</Alert>}
                <Stack spacing={2}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField
                        label={labels.complex}
                        value={reportComplexId}
                        onChange={(e) => setReportComplexId(e.target.value)}
                        select
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="">Авто (активный жилой комплекс)</MenuItem>
                        {(complexes || []).map((c) => (
                          <MenuItem key={c.id} value={String(c.id)}>
                            {c.name || 'Без названия'}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <TextField
                        label="Период отчёта"
                        value={reportPeriod}
                        onChange={(e) => setReportPeriod(e.target.value)}
                        select
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="current_month">Текущий месяц</MenuItem>
                        <MenuItem value="prev_month">Прошлый месяц</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <TextField
                        label="Якорный день"
                        value={reportAnchorDay}
                        onChange={(e) => setReportAnchorDay(e.target.value)}
                        type="number"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 1, max: 31 }}
                        helperText="День месяца для фиксации периода (1–31)"
                      />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <TextField
                        label="Окно подбора, дней"
                        value={reportFallbackDays}
                        onChange={(e) => setReportFallbackDays(e.target.value)}
                        type="number"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, max: 62 }}
                        helperText="Сколько дней назад берём показания (0–62)"
                      />
                    </Grid>
                  </Grid>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="contained"
                      disabled={reportDownloading}
                      onClick={() => handleDownloadReport()}
                      startIcon={<DescriptionRoundedIcon />}
                    >
                      {reportDownloading ? 'Формируем…' : 'Скачать отчёт'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleDownloadReport('prev_month')}
                      startIcon={<EventNoteRoundedIcon />}
                    >
                      Прошлый месяц
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid var(--border-color)',
                  background: 'var(--surface-bg)',
                  height: '100%',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: 'rgba(16, 185, 129, 0.12)',
                      color: '#047857',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AutoGraphRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Регламентные отчёты
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Настройте расписание автоматической отправки
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {`Используйте расписания для регулярной выгрузки отчётов по ${labels.complex.toLowerCase()}.`}
                </Typography>
                  <Alert severity="info">
                    {`Настройки расписаний доступны на странице ${labels.complex.toLowerCase()} (кнопка «Регламентные отчёты»).`}
                  </Alert>
              </Paper>
            </Grid>
          </Grid>
        );
      case 'Статистика':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Paper
                className="glass-sheen"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid var(--border-color)',
                  background: 'var(--surface-bg)',
                  height: '100%',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: 'rgba(14, 165, 233, 0.14)',
                      color: '#0369A1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AutoGraphRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Сводка показаний
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      24 часа, 7 и 30 дней
                    </Typography>
                  </Box>
                </Stack>
                {statsLoading ? (
                  <Stack spacing={1.5}>
                    <Skeleton height={22} />
                    <Skeleton height={22} />
                    <Skeleton height={22} />
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          24 часа (есть показания)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0369A1' }}>
                          {readingsStats.pct24h}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.75,
                          height: 6,
                          borderRadius: 999,
                          background: 'rgba(14, 165, 233, 0.18)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ height: '100%', width: `${readingsStats.pct24h}%`, background: '#0EA5E9' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          7 дней (есть показания)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F766E' }}>
                          {readingsStats.pct7d}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.75,
                          height: 6,
                          borderRadius: 999,
                          background: 'rgba(15, 118, 110, 0.16)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ height: '100%', width: `${readingsStats.pct7d}%`, background: '#0F766E' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          30 дней (есть показания)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1D4ED8' }}>
                          {readingsStats.pct30d}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.75,
                          height: 6,
                          borderRadius: 999,
                          background: 'rgba(59, 130, 246, 0.18)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ height: '100%', width: `${readingsStats.pct30d}%`, background: '#3B82F6' }} />
                      </Box>
                    </Box>
                  </Stack>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper
                className="glass-sheen"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid var(--border-color)',
                  background: 'var(--surface-bg)',
                  height: '100%',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: 'rgba(16, 185, 129, 0.12)',
                      color: '#047857',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <EventNoteRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {labels.coverageComplex}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isSettlementTenant ? 'Дома посёлка с устройствами' : 'Дома и комплексы с устройствами'}
                    </Typography>
                  </Box>
                </Stack>
                {pending ? (
                  <Stack spacing={1.5}>
                    <Skeleton height={22} />
                    <Skeleton height={22} />
                    <Skeleton height={22} />
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Дома с устройствами
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#047857' }}>
                          {coverageStats.buildingsCoverage}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {coverageStats.buildingsWithDevices} из {coverageStats.buildingsTotal}
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.75,
                          height: 6,
                          borderRadius: 999,
                          background: 'rgba(16, 185, 129, 0.16)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ height: '100%', width: `${coverageStats.buildingsCoverage}%`, background: '#10B981' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {labels.complexWithDevices}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0EA5A4' }}>
                          {coverageStats.complexesCoverage}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {coverageStats.complexesWithDevices} из {coverageStats.complexesTotal}
                      </Typography>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Среднее устройств на дом
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F766E' }}>
                          {coverageStats.buildingsTotal > 0
                            ? coverageStats.devicesPerBuilding.toFixed(1)
                            : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper
                className="glass-sheen"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid var(--border-color)',
                  background: 'var(--surface-bg)',
                  height: '100%',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: 'rgba(245, 158, 11, 0.16)',
                      color: '#92400E',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <DescriptionRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Качество данных
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Текущее распределение устройств по состояниям
                    </Typography>
                  </Box>
                </Stack>
                {statsLoading ? (
                  <Stack spacing={1.5}>
                    <Skeleton height={22} />
                    <Skeleton height={22} />
                    <Skeleton height={22} />
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Онлайн
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F766E' }}>
                          {dataQualityStats.onlinePct}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.75,
                          height: 6,
                          borderRadius: 999,
                          background: 'rgba(15, 118, 110, 0.16)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ height: '100%', width: `${dataQualityStats.onlinePct}%`, background: '#0F766E' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Оффлайн
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#B45309' }}>
                          {dataQualityStats.offlinePct}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.75,
                          height: 6,
                          borderRadius: 999,
                          background: 'rgba(180, 83, 9, 0.16)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ height: '100%', width: `${dataQualityStats.offlinePct}%`, background: '#F59E0B' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Без показаний
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1D4ED8' }}>
                          {dataQualityStats.noReadingsPct}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {dataQualityStats.noReadings} из {dataQualityStats.total}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };
  const targetComplexId = useMemo(() => {
    if (activeComplexId) return String(activeComplexId);
    const first = complexes?.[0]?.id;
    return first ? String(first) : null;
  }, [activeComplexId, complexes]);
  const [exporting, setExporting] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const searchPageSize = 10;
  const [networkSummary, setNetworkSummary] = useState(null);
  const [networkLoading, setNetworkLoading] = useState(false);
  const statsLoading = pending || networkLoading;
  const [networkError, setNetworkError] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('sidebar-collapsed') === '1';
    } catch (_) {
      return false;
    }
  });
  const [gatewaySummary, setGatewaySummary] = useState(null);
  const [gatewaySummaryLoading, setGatewaySummaryLoading] = useState(false);
  const [gatewaySummaryError, setGatewaySummaryError] = useState('');
  const defaultAlertSettings = useMemo(
    () => ({
      enabled: true,
      notify_down: true,
      notify_degraded: true,
      notify_recovered: true,
      email_recipients: [],
      telegram_chat_ids: [],
      quiet_mode: 'none',
      quiet_hours_start: null,
      quiet_hours_end: null,
    }),
    []
  );
  const [alertSettings, setAlertSettings] = useState(defaultAlertSettings);
  const [alertSettingsLoading, setAlertSettingsLoading] = useState(false);
  const [alertSettingsError, setAlertSettingsError] = useState('');
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertSaving, setAlertSaving] = useState(false);
  const [uspdItems, setUspdItems] = useState([]);
  const [uspdLoading, setUspdLoading] = useState(false);
  const [uspdError, setUspdError] = useState('');
  const [reportComplexId, setReportComplexId] = useState('');
  const [reportPeriod, setReportPeriod] = useState('current_month');
  const [reportAnchorDay, setReportAnchorDay] = useState('');
  const [reportFallbackDays, setReportFallbackDays] = useState('');
  const [reportDownloading, setReportDownloading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsMenuOpen = Boolean(actionsAnchorEl);
  const handleOpenAlertDialog = useCallback(() => {
    setAlertDialogOpen(true);
  }, []);
  const handleCloseAlertDialog = useCallback(() => {
    setAlertDialogOpen(false);
  }, []);
  const handleSaveAlertSettings = useCallback(async (payload) => {
    setAlertSaving(true);
    setAlertSettingsError('');
    try {
      const data = await gatewaysApi.updateAlertSettings(payload);
      const merged = { ...defaultAlertSettings, ...data };
      setAlertSettings(merged);
      setAlertDialogOpen(false);
      showSoftSuccess('Уведомления сохранены');
    } catch (error) {
      setAlertSettingsError(
        error?.response?.data?.detail || error?.message || 'Не удалось сохранить настройки'
      );
    } finally {
      setAlertSaving(false);
    }
  }, [defaultAlertSettings]);
  const deviceStateStats = useMemo(() => {
    const total = Number(networkSummary?.total ?? summary.devices ?? 0);
    const online = Math.max(0, Number(networkSummary?.online ?? 0));
    const offline = Math.max(0, Number(networkSummary?.offline ?? 0));
    const newly = Math.max(0, Number(networkSummary?.new ?? 0));
    // Backend no_readings = warn + NEW; keep raw warn for diagnostics if needed.
    const noReadings = Math.max(0, Number(networkSummary?.no_readings ?? 0));
    const noReadingsRaw = Math.max(0, noReadings - newly);
    const safeTotal = Math.max(1, total);
    const onlinePct = total > 0 ? Math.round((online / safeTotal) * 100) : 0;
    const offlinePct = total > 0 ? Math.round((offline / safeTotal) * 100) : 0;
    const noReadingsPct = Math.max(0, 100 - onlinePct - offlinePct);
    return {
      total,
      online,
      offline,
      noReadings,
      noReadingsRaw,
      newly,
      onlinePct,
      offlinePct,
      noReadingsPct,
    };
  }, [networkSummary, summary.devices]);
  const readingsStats = useMemo(() => {
    const total = Number(networkSummary?.total ?? summary.devices ?? 0);
    const with24h = Math.max(0, Number(networkSummary?.readings_last_24h ?? 0));
    const with7d = Math.max(0, Number(networkSummary?.readings_last_7d ?? 0));
    const with30d = Math.max(0, Number(networkSummary?.readings_last_30d ?? 0));
    const pct24h = total > 0 ? Math.round((with24h / total) * 100) : 0;
    const pct7d = total > 0 ? Math.round((with7d / total) * 100) : 0;
    const pct30d = total > 0 ? Math.round((with30d / total) * 100) : 0;
    return {
      total,
      with24h,
      with7d,
      with30d,
      pct24h,
      pct7d,
      pct30d,
    };
  }, [networkSummary, summary.devices]);
  const coverageStats = useMemo(() => {
    let buildingsTotal = 0;
    let buildingsWithDevices = 0;
    let complexesWithDevices = 0;
    let totalDevices = 0;
    const complexList = complexes || [];
    complexList.forEach((complex) => {
      const deviceTotal = Number(complex?.device_counts?.total ?? complex?.device_count ?? 0);
      totalDevices += deviceTotal;
      if (deviceTotal > 0) complexesWithDevices += 1;
      const buildingList = complex?.buildings || [];
      buildingsTotal += buildingList.length;
      buildingList.forEach((building) => {
        const buildingDevices = Number(building?.device_counts?.total ?? building?.device_count ?? 0);
        if (buildingDevices > 0) buildingsWithDevices += 1;
      });
    });
    const buildingsCoverage = buildingsTotal > 0
      ? Math.round((buildingsWithDevices / buildingsTotal) * 100)
      : 0;
    const complexesCoverage = complexList.length > 0
      ? Math.round((complexesWithDevices / complexList.length) * 100)
      : 0;
    const devicesPerBuilding = buildingsTotal > 0 ? totalDevices / buildingsTotal : 0;
    return {
      buildingsTotal,
      buildingsWithDevices,
      buildingsCoverage,
      complexesTotal: complexList.length,
      complexesWithDevices,
      complexesCoverage,
      devicesPerBuilding,
      totalDevices,
    };
  }, [complexes]);
  const dataQualityStats = useMemo(() => {
    return {
      online: deviceStateStats.online,
      onlinePct: deviceStateStats.onlinePct,
      offline: deviceStateStats.offline,
      offlinePct: deviceStateStats.offlinePct,
      noReadings: deviceStateStats.noReadings,
      noReadingsPct: deviceStateStats.noReadingsPct,
      total: deviceStateStats.total,
    };
  }, [deviceStateStats]);
  const gatewayBanner = useMemo(() => {
    const down = Math.max(0, Number(gatewaySummary?.down ?? 0));
    const degraded = Math.max(0, Number(gatewaySummary?.degraded ?? 0));
    const total = Math.max(0, Number(gatewaySummary?.total ?? 0));
    if (down > 0) {
      return {
        severity: 'error',
        title: `Сеть деградирует: ${down} шлюзов DOWN`,
        subtitle: `Всего шлюзов: ${total}.`,
      };
    }
    if (degraded > 0) {
      return {
        severity: 'warning',
        title: `Сеть нестабильна: ${degraded} шлюзов DEGRADED`,
        subtitle: `Всего шлюзов: ${total}.`,
      };
    }
    return null;
  }, [gatewaySummary]);
  const widgetDefaults = {
    indicators: true,
    network: true,
    quickActions: true,
    quality: true,
  };
  const [widgets, setWidgets] = useState(() => {
    try {
      const raw = localStorage.getItem('home.widgets');
      return raw ? { ...widgetDefaults, ...JSON.parse(raw) } : widgetDefaults;
    } catch (_) {
      return widgetDefaults;
    }
  });
  useEffect(() => {
    const handler = () => {
      try {
        const raw = localStorage.getItem('home.widgets');
        setWidgets(raw ? { ...widgetDefaults, ...JSON.parse(raw) } : widgetDefaults);
      } catch (_) {
        setWidgets(widgetDefaults);
      }
    };
    window.addEventListener('home-widgets-changed', handler);
    return () => window.removeEventListener('home-widgets-changed', handler);
  }, []);
  useEffect(() => {
    let active = true;
    const loadStatusSummary = async () => {
      setNetworkLoading(true);
      setNetworkError('');
      try {
        const data = await devicesApi.getStatusSummary();
        if (active) {
          setNetworkSummary(data);
        }
      } catch (error) {
        if (active) {
          setNetworkError(error?.message || 'Не удалось получить состояние сети');
        }
      } finally {
        if (active) {
          setNetworkLoading(false);
        }
      }
    };
    loadStatusSummary();
    return () => {
      active = false;
    };
  }, [activeTenant?.tenant_id, activeTenant?.tenant_schema]);
  useEffect(() => {
    const syncSidebarState = (event) => {
      if (typeof event?.detail?.collapsed === 'boolean') {
        setSidebarCollapsed(event.detail.collapsed);
        return;
      }
      try {
        setSidebarCollapsed(window.localStorage.getItem('sidebar-collapsed') === '1');
      } catch (_) {
        setSidebarCollapsed(false);
      }
    };
    syncSidebarState();
    window.addEventListener('sidebar-state-changed', syncSidebarState);
    window.addEventListener('storage', syncSidebarState);
    return () => {
      window.removeEventListener('sidebar-state-changed', syncSidebarState);
      window.removeEventListener('storage', syncSidebarState);
    };
  }, []);
  useEffect(() => {
    let active = true;
    let timerId;
    const loadGatewaySummary = async () => {
      setGatewaySummaryLoading(true);
      setGatewaySummaryError('');
      try {
        const data = await gatewaysApi.statusSummary();
        if (active) {
          setGatewaySummary(data);
        }
      } catch (error) {
        if (active) {
          setGatewaySummaryError(error?.message || 'Не удалось получить статус шлюзов');
        }
      } finally {
        if (active) {
          setGatewaySummaryLoading(false);
        }
      }
    };
    loadGatewaySummary();
    timerId = setInterval(loadGatewaySummary, 60000);
    return () => {
      active = false;
      if (timerId) clearInterval(timerId);
    };
  }, [activeTenant?.tenant_id, activeTenant?.tenant_schema]);
  useEffect(() => {
    if (!canManage) return;
    let active = true;
    const loadAlertSettings = async () => {
      setAlertSettingsLoading(true);
      setAlertSettingsError('');
      try {
          const data = await gatewaysApi.getAlertSettings();
        if (active) {
          const merged = { ...defaultAlertSettings, ...data };
          setAlertSettings(merged);
        }
      } catch (error) {
        if (active) {
          setAlertSettingsError(error?.message || 'Не удалось загрузить настройки уведомлений');
        }
      } finally {
        if (active) setAlertSettingsLoading(false);
      }
    };
    loadAlertSettings();
    return () => {
      active = false;
    };
  }, [canManage, activeTenant?.tenant_id, activeTenant?.tenant_schema, defaultAlertSettings]);
  const loadUspd = useCallback(async () => {
    setUspdLoading(true);
    setUspdError('');
    try {
      const params = { gateway_type: 'USPD' };
      if (activeComplexId) {
        params.residential_complex_id = String(activeComplexId);
      }
      const data = await gatewaysApi.list(params);
      setUspdItems(Array.isArray(data) ? data : []);
    } catch (error) {
      setUspdItems([]);
      setUspdError(error?.message || 'Не удалось загрузить список УСПД');
    } finally {
      setUspdLoading(false);
    }
  }, [activeComplexId]);
  useEffect(() => {
    if (activeTab === 'Список УСПД') {
      loadUspd();
    }
  }, [activeTab, loadUspd]);
  const handleExport = useCallback(async () => {
    if (!targetComplexId) {
      console.warn('Нет данных для выгрузки отчёта по ЖК');
      return;
    }
    try {
      setExporting(true);
      const { data, fileName } = await reportsApi.downloadResidentialConsumptionReport({
        residentialComplexId: targetComplexId,
      });
      const fallbackBase = buildFileName(['consumption', targetComplexId], 'consumption-report');
      const finalName = typeof fileName === 'string' && fileName.trim().length
        ? fileName.trim()
        : `${fallbackBase}.xlsx`;
      downloadBlobAsFile(data, finalName);
    } catch (error) {
      console.error('Не удалось выгрузить отчёт по ЖК', error);
    } finally {
      setExporting(false);
    }
  }, [targetComplexId]);
  const resolvePeriodDate = useCallback((periodKey) => {
    return localMonthStartDate(periodKey === 'prev_month' ? 1 : 0);
  }, []);
  const handleDownloadReport = useCallback((presetPeriod) => {
    const periodKey = presetPeriod || reportPeriod;
    const periodDate = resolvePeriodDate(periodKey);
    const complexId = reportComplexId || targetComplexId;
    if (!complexId) {
      setReportError(`Выберите ${labels.complex.toLowerCase()} для выгрузки отчёта.`);
      return;
    }
    setReportError('');
    setReportDownloading(true);
    (async () => {
      try {
        const { data, fileName } = await reportsApi.downloadResidentialConsumptionReport({
          residentialComplexId: String(complexId),
          period: periodDate,
          anchorDay: reportAnchorDay ? Number(reportAnchorDay) : undefined,
          fallbackDays: reportFallbackDays ? Number(reportFallbackDays) : undefined,
        });
        const fallbackBase = buildFileName(['consumption', String(complexId)], 'consumption-report');
        const finalName = typeof fileName === 'string' && fileName.trim().length
          ? fileName.trim()
          : `${fallbackBase}.xlsx`;
        downloadBlobAsFile(data, finalName);
      } catch (error) {
        setReportError(error?.message || 'Не удалось выгрузить отчёт.');
      } finally {
        setReportDownloading(false);
      }
    })();
  }, [reportComplexId, targetComplexId, reportPeriod, reportAnchorDay, reportFallbackDays, resolvePeriodDate]);
  const clearSearchState = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchPage(1);
    setSearchTotal(0);
    setSearchError('');
    setSearchLoading(false);
  }, []);
  const performSearch = useCallback(
    async (query, page = 1) => {
      if (!query) {
        clearSearchState();
        return;
      }
      setSearchLoading(true);
      setSearchError('');
      try {
        const data = await devicesApi.searchDevices(query, {
          page,
          page_size: searchPageSize,
        });
        setSearchResults(data.items || []);
        setSearchTotal(data.total || 0);
        setSearchPage(data.page || 1);
      } catch (error) {
        setSearchError(error?.message || 'Не удалось выполнить поиск');
        setSearchResults([]);
        setSearchTotal(0);
      } finally {
        setSearchLoading(false);
      }
    },
    [clearSearchState]
  );
  const handleSearchChange = useCallback(
    (value) => {
      setSearchQuery(value);
      performSearch(value, 1);
    },
    [performSearch]
  );
  const handleSearchToggle = useCallback(
    (open) => {
      setSearchActive(open);
      if (!open) {
        clearSearchState();
      }
    },
    [clearSearchState]
  );
  const quickActions = useMemo(() => {
    const actions = [];
    if (canManage) {
      actions.push({
        key: 'add-complex',
        label: 'Новый комплекс',
        onClick: () => navigate('/admin/add-complex'),
      });
    }
    actions.push({
      key: 'export-report',
      label: 'Сформировать отчёт',
      onClick: handleExport,
      disabled: !targetComplexId,
    });
    if (canWrite) {
      actions.push({
        key: 'search-devices',
        label: 'Поиск устройств',
        onClick: () => handleSearchToggle(true),
      });
    }
    return actions;
  }, [canManage, canWrite, handleExport, handleSearchToggle, navigate, targetComplexId]);
  const handleOpenActionsMenu = useCallback((event) => {
    setActionsAnchorEl(event.currentTarget);
  }, []);
  const handleCloseActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
  }, []);
  const handleNavigateToDevice = useCallback(
    (device) => {
      if (device?.apartment_id) {
        navigate(`/apartments/${device.apartment_id}?deviceId=${device.id}`);
        return;
      }
      if (!device?.corps_id) return;
      const buildingId = device?.building_id ? `?buildingId=${device.building_id}` : '';
      navigate(`/corps/${device.corps_id}${buildingId}`);
    },
    [navigate]
  );
  if (showOnboarding) {
    return (
      <OnboardingPage
        showDismiss
        onDismiss={handleOnboardingDismiss}
        onComplete={markOnboardingSeen}
      />
    );
  }
  return (
    <Box
      className="home-container page-animate"
      sx={{
        px: { xs: 2, md: 3 },
        pb: 5,
        width: '100%',
        minHeight: '100vh',
        background: 'transparent',
      }}
    >
      {/* <h1 style={{ marginBottom: 8 }}>Выбор ЖК</h1> */}
      {/* <AppHeader
        title="Выбор ЖК"
        user={user}
        onGoToUsers={()=>{}}
        onLogout={}
      /> */}
      <HomeGreeting user={user} />
      <Paper
        className="card-pop"
        style={{ animationDelay: '40ms' }}
        elevation={0}
        sx={{
          p: { xs: 1, md: 0.5 },
          mb: 1.25,
          borderRadius: 0,
          border: 'none',
          background: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="flex-end" spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {isContractor && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 99,
                  border: '1px solid var(--border-color)',
                  background: 'var(--surface-elev)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-color)',
                }}
              >
                Клиент: {activeTenant?.tenant_name || activeTenant?.tenant_schema || 'не выбран'}
              </Box>
            )}
            {activeTenantAccessLevel && (
              <Box
                sx={{
                  px: 1.25,
                  py: 0.45,
                  borderRadius: 99,
                  border: '1px solid var(--border-color)',
                  background: activeTenantAccessLevel === 'write'
                    ? 'rgba(16, 185, 129, 0.12)'
                    : 'rgba(100, 116, 139, 0.12)',
                  fontSize: 12,
                  fontWeight: 600,
                  color: activeTenantAccessLevel === 'write' ? '#047857' : '#475569',
                }}
              >
                {activeTenantAccessLevel === 'write' ? 'Редактирование' : 'Только чтение'}
              </Box>
            )}
          </Stack>
        </Stack>
      </Paper>
      {gatewaySummaryError && !gatewaySummaryLoading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {gatewaySummaryError}
        </Alert>
      )}
      {gatewayBanner && (
        <Paper
          className="glass-sheen card-pop"
          style={{ animationDelay: '60ms' }}
          sx={{
            p: { xs: 2, md: 2.25 },
            mb: 2,
            borderRadius: 3,
            border: '1px solid var(--border-color)',
            background:
              gatewayBanner.severity === 'error'
                ? 'linear-gradient(120deg, rgba(254, 202, 202, 0.4), rgba(254, 226, 226, 0.9))'
                : 'linear-gradient(120deg, rgba(254, 243, 199, 0.35), rgba(254, 243, 199, 0.8))',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {gatewayBanner.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {gatewayBanner.subtitle}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Button variant="outlined" size="small" onClick={() => navigate('/gateways')}>
                Открыть шлюзы
              </Button>
              {canManage && (
                <Button variant="contained" size="small" onClick={handleOpenAlertDialog}>
                  Настроить уведомления
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {widgets.indicators && (
          <Grid item xs={12} md={8} lg={8}>
            <Paper
              className="glass-sheen card-pop"
              style={{ animationDelay: '80ms' }}
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid var(--border-color)',
                background: 'var(--surface-bg)',
                overflow: 'hidden',
                position: 'relative',
                minHeight: 150,
                boxShadow: 'none',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Показатели ресурсов
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                  7 дней
                </Typography>
              </Stack>
            {pending ? (
              <Box sx={{ overflow: 'hidden' }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{
                    overflowX: 'hidden',
                    overflowY: 'hidden',
                    pb: 0.5,
                    pr: 0.5,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-vertical': { width: 0 },
                  }}
                >
                  {Array.from({ length: indicatorCards.length }).map((_, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      className="card-pop"
                      style={{ animationDelay: `${120 + idx * 60}ms` }}
                      sx={{
                        minWidth: 124,
                        flex: '0 0 auto',
                        p: 1.1,
                        borderRadius: 2,
                        borderColor: 'var(--border-color)',
                        background: '#FFFFFF',
                        boxShadow: 'none',
                      }}
                    >
                      <Skeleton width="70%" />
                      <Skeleton height={32} />
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  pb: 0.5,
                  '&::-webkit-scrollbar': { height: 6 },
                  '&::-webkit-scrollbar-vertical': { width: 0 },
                }}
              >
                {indicatorCards.map((item) => {
                  const Icon = item.icon;
                  return (
                      <Paper
                        key={item.key}
                        variant="outlined"
                        className="card-pop"
                        style={{ animationDelay: `${120 + indicatorCards.indexOf(item) * 60}ms` }}
                        sx={{
                          minWidth: 120,
                          flex: '0 0 auto',
                          p: 1.1,
                          borderRadius: 2,
                          borderColor: 'var(--border-color)',
                          background: '#FFFFFF',
                          display: 'grid',
                          gap: 0.45,
                          boxShadow: 'none',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: 1.5,
                              display: 'grid',
                              placeItems: 'center',
                              background: `${item.color}22`,
                              color: item.color,
                            }}
                          >
                            <Icon fontSize="small" />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                            {item.label}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h5" sx={{ fontWeight: 500, lineHeight: 1 }}>
                            <AnimatedNumber value={item.value} />
                          </Typography>
                          {renderSparkline(item.series, item.color)}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Grid>
        )}
        {false && widgets.quickActions && (
          <Grid item xs={12} md={quickActionCount > 2 ? 4 : 2} lg={quickActionCount > 2 ? 4 : 2}>
            <Paper
              className="glass-sheen card-pop"
              style={{ animationDelay: '140ms' }}
              sx={{
                p: 2.25,
                borderRadius: 3,
                border: '1px solid var(--border-color)',
                background: 'var(--surface-bg)',
                height: quickActionCount <= 2 ? 'fit-content' : '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      background: 'rgba(59, 130, 246, 0.12)',
                      color: '#1D4ED8',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      fontWeight: 700,
                      fontSize: 16,
                    }}
                  >
                    ⚡
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Быстрые действия
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Частые операции и отчёты · {quickActionCount} доступно
                    </Typography>
                  </Box>
                </Stack>
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleOpenActionsMenu}
                    endIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: 999,
                      px: 2.5,
                      boxShadow: 'none',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Действия
                  </Button>
                  <Menu
                    anchorEl={actionsAnchorEl}
                    open={actionsMenuOpen}
                    onClose={handleCloseActionsMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {quickActions.map((action) => (
                      <MenuItem
                        key={action.key}
                        disabled={action.disabled}
                        onClick={() => {
                          handleCloseActionsMenu();
                          action.onClick();
                        }}
                      >
                        {action.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        )}
        {widgets.quality && (
          <Grid item xs={12} md={4} lg={4}>
            <Paper
              className="glass-sheen card-pop"
              style={{ animationDelay: '200ms' }}
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid var(--border-color)',
                background: 'var(--surface-bg)',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'none',
              }}
            >
              {pending ? (
                <Stack spacing={1}>
                  <Skeleton height={82} />
                  <Skeleton height={82} />
                </Stack>
                ) : (
                  <Stack spacing={1}>
                    {[
                      {
                        key: 'fresh',
                        label: 'Актуальные',
                        value: dataQualityStats.online,
                        percent: dataQualityStats.onlinePct,
                        color: '#6FD34D',
                        track: 'rgba(111, 211, 77, 0.16)',
                      },
                      {
                        key: 'missed',
                        label: 'Пропущенные',
                        value: dataQualityStats.offline + dataQualityStats.noReadings,
                        percent: Math.min(100, dataQualityStats.offlinePct + dataQualityStats.noReadingsPct),
                        color: '#EC4899',
                        track: 'rgba(236, 72, 153, 0.16)',
                      },
                    ].map((item, index) => (
                    <React.Fragment key={item.key}>
                      {index > 0 && <Divider />}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={54}
                            thickness={4}
                            sx={{ color: '#E9EDF5' }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={Math.max(0, Math.min(100, item.percent))}
                            size={54}
                            thickness={4}
                            sx={{
                              color: item.color,
                              position: 'absolute',
                              left: 0,
                            }}
                          />
                          <Box
                            sx={{
                              inset: 0,
                              position: 'absolute',
                              display: 'grid',
                              placeItems: 'center',
                              color: item.color,
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                background: item.track,
                                fontSize: 12,
                              }}
                            >
                              {item.key === 'fresh' ? '👍' : '⚠'}
                            </Box>
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 500, lineHeight: 1 }}>
                            {item.value.toLocaleString('ru-RU')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                            {item.label}
                          </Typography>
                        </Box>
                      </Stack>
                    </React.Fragment>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
      {widgets.network && (
        <Paper
          className="glass-sheen card-pop"
          style={{ animationDelay: '240ms' }}
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 2.5,
            border: '1px solid var(--border-color)',
            background: 'var(--surface-bg)',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: 'none',
            '&:hover .network-widget-actions': canManage && !sidebarCollapsed
              ? {
                  opacity: 1,
                  pointerEvents: 'auto',
                  width: 48,
                  minWidth: 48,
                  transform: 'translateX(0)',
                }
              : undefined,
            '&:hover .network-widget-content': canManage && !sidebarCollapsed
              ? {
                  transform: 'translateX(-12px)',
                }
              : undefined,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.35 }}>
                Состояние сети
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                Статистика устройств приёма показаний (УСПД) и доступность
              </Typography>
            </Box>
            {pending || networkLoading ? (
              <Stack direction="row" spacing={1.5}>
                <Skeleton width={96} height={36} />
                <Skeleton width={96} height={36} />
                <Skeleton width={120} height={36} />
              </Stack>
            ) : networkError ? (
              <Typography variant="body2" color="text.secondary">
                {networkError}
              </Typography>
            ) : (
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.25}
                flexWrap="wrap"
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  position: 'relative',
                }}
              >
                <Stack
                  className="network-widget-content"
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.25}
                  flexWrap="wrap"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    pr: canManage && !sidebarCollapsed ? { md: 0.5 } : 0,
                    transition: 'transform 180ms ease',
                  }}
                >
                {(() => {
                  const total = Math.max(0, networkSummary?.total || 0);
                  const online = Math.max(0, networkSummary?.online || 0);
                  const offline = Math.max(0, networkSummary?.offline || 0);
                  const noReadings = Math.max(0, networkSummary?.no_readings || 0);
                  const base = Math.max(1, total || online + offline + noReadings);
                  const onlinePct = Math.round((online / base) * 100);
                  const offlinePct = Math.round((offline / base) * 100);
                  const noReadingsPct = Math.max(0, 100 - onlinePct - offlinePct);
                  return (
                    <>
                      <Tooltip title="Устройства, которые сейчас на связи и передают данные штатно." arrow>
                        <Box sx={{ px: 1.1, py: 0.55, borderRadius: 1.5, background: 'rgba(111, 211, 77, 0.28)', color: '#235B1B', fontWeight: 500, fontSize: 12, cursor: 'help' }}>
                          Онлайн: {online}
                        </Box>
                      </Tooltip>
                      <Tooltip title="Устройства недоступны: нет связи или не проходит обмен данными." arrow>
                        <Box sx={{ px: 1.1, py: 0.55, borderRadius: 1.5, background: 'rgba(255, 224, 143, 0.58)', color: '#624A00', fontWeight: 500, fontSize: 12, cursor: 'help' }}>
                          Оффлайн: {offline}
                        </Box>
                      </Tooltip>
                      <Tooltip title="Устройства есть в системе, но по ним ещё нет показаний или они давно не поступали." arrow>
                        <Box sx={{ px: 1.1, py: 0.55, borderRadius: 1.5, background: 'rgba(246, 210, 216, 0.72)', color: '#7A3641', fontWeight: 500, fontSize: 12, cursor: 'help' }}>
                          Без показаний: {noReadings}
                        </Box>
                      </Tooltip>
                      <Box sx={{ minWidth: 240, flex: 1 }}>
                        <Box
                          sx={{
                            mt: 0.75,
                            height: 10,
                            borderRadius: 999,
                            background: 'rgba(148, 163, 184, 0.22)',
                            overflow: 'hidden',
                            display: 'flex',
                            boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.08)',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${onlinePct}%`,
                              background: 'linear-gradient(90deg, #34D399 0%, #10B981 100%)',
                            }}
                          />
                          <Box
                            sx={{
                              width: `${offlinePct}%`,
                              background: 'linear-gradient(90deg, #FCD34D 0%, #F59E0B 100%)',
                            }}
                          />
                          <Box
                            sx={{
                              width: `${noReadingsPct}%`,
                              background: 'linear-gradient(90deg, #F9A8B4 0%, #EC4899 100%)',
                            }}
                          />
                        </Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                          sx={{ mt: 0.85 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Всего устройств: {total}
                          </Typography>
                          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981' }} />
                              <Typography variant="caption" color="text.secondary">
                                {onlinePct}%
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B' }} />
                              <Typography variant="caption" color="text.secondary">
                                {offlinePct}%
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#EC4899' }} />
                              <Typography variant="caption" color="text.secondary">
                                {noReadingsPct}%
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>
                    </>
                  );
                })()}
                </Stack>
                {canManage && sidebarCollapsed && (
                  <Button
                    variant="contained"
                    onClick={handleOpenAlertDialog}
                    sx={{
                      minWidth: 124,
                      minHeight: 40,
                      borderRadius: 1.75,
                      px: 1.5,
                      fontSize: 13,
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: 'none',
                    }}
                  >
                    Уведомления
                  </Button>
                )}
                {canManage && !sidebarCollapsed && (
                  <Box
                    className="network-widget-actions"
                    sx={{
                      width: 0,
                      minWidth: 0,
                      overflow: 'hidden',
                      opacity: 0,
                      pointerEvents: 'none',
                      transform: 'translateX(10px)',
                      transition:
                        'width 180ms ease, min-width 180ms ease, transform 180ms ease, opacity 180ms ease',
                      alignSelf: { xs: 'stretch', md: 'center' },
                      display: { xs: 'none', md: 'flex' },
                      justifyContent: 'flex-end',
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleOpenAlertDialog}
                      sx={{
                        minWidth: 44,
                        width: 44,
                        height: 44,
                        borderRadius: 2.25,
                        px: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: '0 12px 24px rgba(37, 99, 235, 0.18)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <NotificationsActiveRoundedIcon sx={{ fontSize: 20 }} />
                    </Button>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1, md: 1.15 },
          py: 0.65,
          mb: 1.25,
          borderRadius: 2.5,
          border: '1px solid rgba(38, 65, 94, 0.12)',
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%)',
          boxShadow: 'none',
        }}
      >
        <ComplexesToolbar
          activeTab={activeTab}
          listTabLabel={labels.complexListTab}
          searchPlaceholder={
            isSettlementTenant
              ? 'Поиск по устройствам, участкам, приборным данным...'
              : 'Поиск по устройствам, квартирам, приборным данным...'
          }
          onTabChange={handleTabChange}
          onExport={handleExport}
          exportLoading={exporting}
          exportDisabled={!targetComplexId}
          searchEnabled
          searchValue={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchToggle={handleSearchToggle}
          onSearch={() => { }}
          onFilter={() => { }}
        />
      </Paper>
      {searchActive ? (
        <Fade in>
          <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Результаты поиска</Typography>
            <Button size="small" onClick={() => handleSearchToggle(false)}>
              Закрыть
            </Button>
          </Stack>
          {!searchQuery ? (
            <Typography color="text.secondary">Введите текст, чтобы найти устройство</Typography>
          ) : searchError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          ) : null}
          {searchQuery && (searchLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : searchResults.length ? (
            <>
              <Stack spacing={1.5}>
                {searchResults.map((device, idx) => (
                  <Fade in key={device.id} style={{ transitionDelay: `${idx * 60}ms` }}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <div>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {device.serial_number || device.dev_eui || 'Без номера'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {device.building_name ? `${device.building_name}` : 'Неизвестный дом'}
                          {device.corp_name && device.corp_name !== 'Без корпуса'
                            ? ` • Корпус ${device.corp_name}`
                            : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Квартира: {device.room_number ?? '—'} {device.suffix || ''}
                        </Typography>
                      </div>
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" size="small" onClick={() => handleNavigateToDevice(device)}>
                          Открыть
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                  </Fade>
                ))}
              </Stack>
              {searchTotal > searchPageSize && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack alignItems="center">
                    <Pagination
                      count={Math.max(1, Math.ceil(searchTotal / searchPageSize))}
                      page={searchPage}
                      onChange={(_, p) => {
                        setSearchPage(p);
                        performSearch(searchQuery, p);
                      }}
                    />
                  </Stack>
                </>
              )}
            </>
          ) : (
            <Typography color="text.secondary">Ничего не найдено</Typography>
          ))}
        </Paper>
        </Fade>
      ) : (
        <div className="rc-fluid">
          <div className="rc-tabpanel" key={activeTab}>
            {renderTab()}
          </div>
        </div>
      )}
      <AlertSettingsDialog
        open={alertDialogOpen}
        loading={alertSaving || alertSettingsLoading}
        error={alertSettingsError}
        initialSettings={alertSettings}
        onClose={handleCloseAlertDialog}
        onSave={handleSaveAlertSettings}
      />
    </Box>
  );
};
export default Home;
